/**
 * AIContextInspector
 *
 * Collapsible debug panel showing all variables and MCP tools available to an AI node at runtime.
 * Helps troubleshoot what context is passed to skills/specialists without cluttering the main editor.
 *
 * Variables follow the Velaro <<scope.name>> syntax.
 * MCP tool names are shown so users can reference them in system prompts.
 */
import React, { useState } from "react";

interface Props {
  /** Tool names from all enabled MCP servers on this node. */
  mcpToolNames?: string[];
}

const BUILTIN_VARS = [
  {
    scope: "conversation",
    color: "blue",
    vars: [
      { name: "conversation.id", desc: "Unique ID for this conversation" },
      { name: "conversation.channel", desc: "Channel: web, sms, whatsapp, email" },
      { name: "conversation.subject", desc: "Subject or initial visitor message" },
      { name: "conversation.startedAt", desc: "ISO timestamp when conversation started" },
      { name: "conversation.referrer", desc: "Page URL when visitor started the chat" },
      { name: "conversation.siteId", desc: "Site ID this conversation belongs to" }
    ]
  },
  {
    scope: "contact",
    color: "purple",
    vars: [
      { name: "contact.id", desc: "Contact record ID" },
      { name: "contact.firstName", desc: "First name (if known)" },
      { name: "contact.lastName", desc: "Last name (if known)" },
      { name: "contact.email", desc: "Email address (if known)" },
      { name: "contact.phone", desc: "Phone number (if known)" },
      { name: "contact.company", desc: "Company name (if known)" }
    ]
  },
  {
    scope: "workflow",
    color: "green",
    vars: [
      {
        name: "workflow.*",
        desc: "Custom properties set by Set Data nodes earlier in this workflow. Use the exact property name you defined."
      }
    ]
  }
];

const SCOPE_COLORS: Record<string, string> = {
  blue: "text-blue-700 bg-blue-50 border-blue-200",
  purple: "text-purple-700 bg-purple-50 border-purple-200",
  green: "text-green-700 bg-green-50 border-green-200"
};

export default function AIContextInspector({ mcpToolNames = [] }: Props) {
  const [open, setOpen] = useState(false);

  return (
    <div className="mt-3 border border-slate-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100 text-left transition-colors"
        aria-expanded={open}
        aria-label="Toggle context inspector"
      >
        <div className="flex items-center gap-2">
          <svg className="w-3.5 h-3.5 text-slate-500" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 2a1 1 0 110 2 1 1 0 010-2zm-1 4h2v5H7V7z" />
          </svg>
          <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
            Available Variables &amp; Tools
          </span>
          <span className="text-[10px] bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded font-medium">
            debug
          </span>
        </div>
        <span className="text-slate-400 text-xs">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="p-3 space-y-3 text-xs bg-white border-t border-slate-100">
          <p className="text-slate-500 leading-relaxed">
            Reference these in your AI Config's system prompt using{" "}
            <code className="bg-slate-100 px-1 py-0.5 rounded font-mono text-slate-700">
              {"<<variable.name>>"}
            </code>{" "}
            syntax. The AI will see the actual values at runtime.
          </p>

          {BUILTIN_VARS.map((group) => (
            <div key={group.scope}>
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">
                {group.scope}
              </div>
              <div className="space-y-1">
                {group.vars.map((v) => (
                  <div key={v.name} className="flex items-start gap-2">
                    <code
                      className={`shrink-0 px-1.5 py-0.5 rounded border font-mono text-[11px] ${SCOPE_COLORS[group.color]}`}
                    >
                      {"<<"}
                      {v.name}
                      {">>"}
                    </code>
                    <span className="text-slate-500 pt-0.5 leading-snug">{v.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* MCP Tools section */}
          <div>
            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">
              MCP Tools Enabled on This Node
            </div>
            {mcpToolNames.length > 0 ? (
              <>
                <div className="flex flex-wrap gap-1">
                  {mcpToolNames.map((t) => (
                    <code
                      key={t}
                      className="text-[11px] text-orange-700 bg-orange-50 border border-orange-200 px-1.5 py-0.5 rounded font-mono"
                    >
                      {t}
                    </code>
                  ))}
                </div>
                <p className="text-slate-400 mt-1.5 leading-relaxed">
                  Mention these tools by name in your system prompt to tell the AI when to call
                  them. Example:{" "}
                  <em>
                    "Use <code className="bg-slate-100 px-0.5 rounded">{mcpToolNames[0]}</code> when
                    the visitor asks about…"
                  </em>
                </p>
              </>
            ) : (
              <div className="text-slate-400 bg-slate-50 rounded p-2 border border-slate-100">
                No MCP tools enabled on this node.{" "}
                <a href="/integrations/mcp" className="text-blue-600 hover:underline">
                  Add an MCP server →
                </a>{" "}
                then check it above to give this AI tools to call.
              </div>
            )}
          </div>

          {/* Delegation tools hint */}
          <div className="pt-2 border-t border-slate-100">
            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">
              Delegation Tools (auto-generated)
            </div>
            <p className="text-slate-400 leading-relaxed">
              If you add Specialist Agents below, the system automatically injects{" "}
              <code className="bg-slate-100 px-0.5 rounded font-mono">
                delegate_to_
                {"<name>"}
              </code>{" "}
              tools into this AI's context — you do not need to add them manually.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
